
import { supabase } from '@/integrations/supabase/client';

export interface SubscriptionBookingResult {
  success: boolean;
  error?: string;
  data?: any;
}

export const incrementBookingsUsed = async (applicationId: string, bookingDate?: string): Promise<SubscriptionBookingResult & { subscriptionBookingId?: string }> => {
  try {
    console.log('DEBUG: incrementBookingsUsed called for applicationId:', applicationId, 'bookingDate:', bookingDate);

    // Fetch application and course details to determine subscription cycle and participants
    const { data: application, error: appError } = await supabase
      .from('applications')
      .select(`
        id,
        mentor_id,
        user_id,
        subscription_start_date,
        courses!applications_course_id_fkey(
          subscription_type,
          weekly_call_allowance,
          included_sessions
        )
      `)
      .eq('id', applicationId)
      .single();

    if (appError || !application) {
      console.error('DEBUG: Error fetching application for increment:', appError);
      return { success: false, error: appError?.message || 'Application not found' };
    }

    if (!application.subscription_start_date) {
      console.warn('DEBUG: Application missing subscription_start_date, cannot compute period');
      return { success: false, error: 'Subscription start date not set' };
    }

    const subscriptionType = application.courses?.subscription_type === 'monthly' ? 'monthly' : 'weekly';

    // Compute period start anchored to subscription_start_date and the booking date (or today)
    const date = bookingDate ? new Date(bookingDate) : new Date();
    const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const subStart = new Date(application.subscription_start_date);
    const subStartUTC = new Date(Date.UTC(subStart.getUTCFullYear(), subStart.getUTCMonth(), subStart.getUTCDate()));

    let currentCycleNumber;
    const periodStartUTC = new Date(subStartUTC);
    
    if (subscriptionType === 'monthly') {
      // For monthly subscriptions, calculate months elapsed
      const startYear = subStartUTC.getUTCFullYear();
      const startMonth = subStartUTC.getUTCMonth();
      const currentYear = utcDate.getUTCFullYear();
      const currentMonth = utcDate.getUTCMonth();
      currentCycleNumber = (currentYear - startYear) * 12 + (currentMonth - startMonth);
      periodStartUTC.setUTCMonth(subStartUTC.getUTCMonth() + currentCycleNumber);
    } else {
      // For weekly subscriptions, use day-based calculation
      const daysSinceStart = Math.floor((utcDate.getTime() - subStartUTC.getTime()) / (1000 * 60 * 60 * 24));
      currentCycleNumber = Math.max(0, Math.floor(daysSinceStart / 7));
      periodStartUTC.setUTCDate(subStartUTC.getUTCDate() + currentCycleNumber * 7);
    }
    
    const periodStartString = periodStartUTC.toISOString().slice(0, 10); // YYYY-MM-DD

    console.log('DEBUG: incrementBookingsUsed - computed period_start_date:', {
      subscriptionType,
      currentCycleNumber,
      periodStartString,
      applicationId
    });

    // Find or create the subscription booking record for this application and period
    const { data: existingBooking, error: fetchError } = await supabase
      .from('subscription_bookings')
      .select('*')
      .eq('application_id', applicationId)
      .eq('week_start_date', periodStartString)
      .eq('is_active', true)
      .maybeSingle();

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('DEBUG: Error fetching subscription booking:', fetchError);
      return { success: false, error: fetchError.message };
    }

    const weeklyAllowance = application.courses?.weekly_call_allowance ?? application.courses?.included_sessions ?? 2;

    if (!existingBooking) {
      console.log('DEBUG: No subscription booking found for period, creating new with bookings_used = 1');
      const { data: newBooking, error: insertError } = await supabase
        .from('subscription_bookings')
        .insert({
          application_id: applicationId,
          mentor_id: application.mentor_id,
          mentee_id: application.user_id,
          week_start_date: periodStartString,
          total_allowance: weeklyAllowance,
          bookings_used: 1,
          is_active: true,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (insertError) {
        console.error('DEBUG: Error creating subscription booking:', insertError);
        return { success: false, error: insertError.message };
      }

      console.log('DEBUG: Successfully created and incremented subscription booking:', newBooking);
      return { success: true, data: newBooking, subscriptionBookingId: newBooking.id };
    }

    // Increment bookings_used on existing row
    const newBookingsUsed = (existingBooking.bookings_used || 0) + 1;
    console.log(`DEBUG: Incrementing bookings_used from ${existingBooking.bookings_used} to ${newBookingsUsed}`);

    const { data: updatedBooking, error: updateError } = await supabase
      .from('subscription_bookings')
      .update({ 
        bookings_used: newBookingsUsed,
        updated_at: new Date().toISOString()
      })
      .eq('id', existingBooking.id)
      .select()
      .single();

    if (updateError) {
      console.error('DEBUG: Error updating subscription booking:', updateError);
      return { success: false, error: updateError.message };
    }

    console.log('DEBUG: Successfully incremented bookings_used:', updatedBooking);

    return { success: true, data: updatedBooking, subscriptionBookingId: updatedBooking.id };
  } catch (error) {
    console.error('DEBUG: Exception in incrementBookingsUsed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Decrement bookings_used for the specific period of the provided booking date (Subscription only)
export const decrementBookingsUsed = async (
  applicationId: string,
  subscriptionBookingId?: string,
  bookingDate?: string
): Promise<SubscriptionBookingResult> => {
  try {
    console.log('DEBUG: decrementBookingsUsed called for applicationId:', applicationId, 'subscriptionBookingId:', subscriptionBookingId, 'bookingDate:', bookingDate);

    // If we have a subscription booking ID, use it directly
    if (subscriptionBookingId) {
      console.log('DEBUG: Using stored subscription booking ID:', subscriptionBookingId);
      
      const { data: existingRecord, error: fetchError } = await supabase
        .from('subscription_bookings')
        .select('*')
        .eq('id', subscriptionBookingId)
        .single();

      if (fetchError || !existingRecord) {
        console.error('DEBUG: No subscription booking record found for stored ID:', fetchError);
        return { success: false, error: 'No booking record found for stored ID' };
      }

      // Decrement bookings_used (ensure it doesn't go below 0)
      const newBookingsUsed = Math.max(0, (existingRecord.bookings_used || 0) - 1);
      
      const { data: updatedBooking, error: updateError } = await supabase
        .from('subscription_bookings')
        .update({ 
          bookings_used: newBookingsUsed,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingRecord.id)
        .select()
        .single();

      if (updateError) {
        console.error('DEBUG: Error updating subscription booking for decrement:', updateError);
        return { success: false, error: 'Failed to update booking count' };
      }

      console.log('DEBUG: Successfully decremented booking count from', existingRecord.bookings_used, 'to', newBookingsUsed);
      return { success: true, data: updatedBooking };
    }

    // Fallback to old method if no subscription booking ID provided
    console.log('DEBUG: No subscription booking ID provided, using fallback method');
    
    // Fetch application to compute correct period based on subscription type and start date
    const { data: application, error: appError } = await supabase
      .from('applications')
      .select(`
        id,
        mentor_id,
        user_id,
        subscription_start_date,
        courses!applications_course_id_fkey(
          subscription_type
        )
      `)
      .eq('id', applicationId)
      .single();

    if (appError || !application) {
      console.error('DEBUG: Error fetching application for decrement:', appError);
      return { success: false, error: appError?.message || 'Application not found' };
    }

    if (!application.subscription_start_date) {
      console.warn('DEBUG: Application missing subscription_start_date, cannot compute period');
      return { success: false, error: 'Subscription start date not set' };
    }

    const subscriptionType = application.courses?.subscription_type === 'monthly' ? 'monthly' : 'weekly';

    const date = bookingDate ? new Date(bookingDate) : new Date();
    const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const subStart = new Date(application.subscription_start_date);
    const subStartUTC = new Date(Date.UTC(subStart.getUTCFullYear(), subStart.getUTCMonth(), subStart.getUTCDate()));

    let currentCycleNumber;
    const periodStartUTC = new Date(subStartUTC);
    
    if (subscriptionType === 'monthly') {
      // For monthly subscriptions, calculate months elapsed
      const startYear = subStartUTC.getUTCFullYear();
      const startMonth = subStartUTC.getUTCMonth();
      const currentYear = utcDate.getUTCFullYear();
      const currentMonth = utcDate.getUTCMonth();
      currentCycleNumber = (currentYear - startYear) * 12 + (currentMonth - startMonth);
      periodStartUTC.setUTCMonth(subStartUTC.getUTCMonth() + currentCycleNumber);
    } else {
      // For weekly subscriptions, use day-based calculation
      const daysSinceStart = Math.floor((utcDate.getTime() - subStartUTC.getTime()) / (1000 * 60 * 60 * 24));
      currentCycleNumber = Math.max(0, Math.floor(daysSinceStart / 7));
      periodStartUTC.setUTCDate(subStartUTC.getUTCDate() + currentCycleNumber * 7);
    }
    
    const periodStartString = periodStartUTC.toISOString().slice(0, 10);

    console.log('DEBUG: decrementBookingsUsed - computed period_start_date:', { subscriptionType, currentCycleNumber, periodStartString });

    // Find the subscription booking for this application and period
    const { data: subscriptionBooking, error: fetchError } = await supabase
      .from('subscription_bookings')
      .select('*')
      .eq('application_id', applicationId)
      .eq('week_start_date', periodStartString)
      .eq('is_active', true)
      .maybeSingle();

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('DEBUG: Error fetching subscription booking for decrement:', fetchError);
      return { success: false, error: fetchError.message };
    }

    if (!subscriptionBooking) {
      console.warn('DEBUG: No subscription booking found for given period to decrement', { applicationId, periodStartString });
      return { success: false, error: 'No subscription booking found for this period' };
    }

    const newBookingsUsed = Math.max(0, (subscriptionBooking.bookings_used || 0) - 1);
    console.log(`DEBUG: Decrementing bookings_used from ${subscriptionBooking.bookings_used} to ${newBookingsUsed}`);

    const { data: updatedBooking, error: updateError } = await supabase
      .from('subscription_bookings')
      .update({
        bookings_used: newBookingsUsed,
        updated_at: new Date().toISOString(),
      })
      .eq('id', subscriptionBooking.id)
      .select()
      .single();

    if (updateError) {
      console.error('DEBUG: Error updating subscription booking on decrement:', updateError);
      return { success: false, error: updateError.message };
    }

    console.log('DEBUG: Successfully decremented bookings_used:', updatedBooking);
    return { success: true, data: updatedBooking };
  } catch (error) {
    console.error('DEBUG: Exception in decrementBookingsUsed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const isSubscriptionApplication = async (applicationId: string): Promise<boolean> => {
  try {
    console.log('DEBUG: Checking if application is subscription type:', applicationId);
    
    const { data: application, error } = await supabase
      .from('applications')
      .select(`
        courses!applications_course_id_fkey(course_type)
      `)
      .eq('id', applicationId)
      .single();

    if (error) {
      console.error('DEBUG: Error checking application type:', error);
      return false;
    }

    const isSubscription = application?.courses?.course_type === 'subscription';
    console.log('DEBUG: Application is subscription:', isSubscription);
    
    return isSubscription;
  } catch (error) {
    console.error('DEBUG: Exception in isSubscriptionApplication:', error);
    return false;
  }
};

export const dispatchSubscriptionBookingUpdate = (applicationId: string, mentorId: string, menteeId: string) => {
  console.log('DEBUG: Dispatching subscription-booking-updated event for:', { applicationId, mentorId, menteeId });
  
  const event = new CustomEvent('subscription-booking-updated', {
    detail: {
      applicationId,
      mentorId,
      menteeId,
      timestamp: new Date().toISOString()
    }
  });
  
  window.dispatchEvent(event);
};

export const dispatchSubscriptionEndedEvent = (subscriptionId: string, mentorId: string, menteeId: string) => {
  console.log('DEBUG: Dispatching subscription-ended event for:', { subscriptionId, mentorId, menteeId });
  
  const event = new CustomEvent('subscription-ended', {
    detail: {
      subscriptionId,
      mentorId,
      menteeId,
      timestamp: new Date().toISOString()
    }
  });
  
  window.dispatchEvent(event);
};
